import { PRINT_COLORS } from '../snapshots';
import { attrId, FIGURE_WIDTH, figureTitle, footLines, LINK_DASH, n1, svgFrame, svgLabel, wrapText } from './svg';

// Box-and-arrow schematics: boxes in columns (left to right: who, through what, to whom), joined by typed links.
// Solid = function or affiliation, dashed = funding or contract, dotted = access or meeting, long dash = procedure
// or pressure. Laid out from a small spec: each box names its column (0…), boxes of a column stack in the order
// given, links run box to box with an optional label. A legend names the four link kinds. Pure and deterministic.

export const SCHEMATIC_LINK_KINDS = ['function', 'funding', 'access', 'procedure'] as const;
export type SchematicLinkKind = (typeof SCHEMATIC_LINK_KINDS)[number];
export const isSchematicLinkKind = (v: unknown): v is SchematicLinkKind => SCHEMATIC_LINK_KINDS.includes(v as SchematicLinkKind);

export const SCHEMATIC_LINK_LABELS: Record<'en' | 'fr', Record<SchematicLinkKind, string>> = {
  en: { function: 'Function / affiliation', funding: 'Funding / contract', access: 'Access / meeting', procedure: 'Procedure / pressure' },
  fr: {
    function: 'Fonction / affiliation',
    funding: 'Financement / contrat',
    access: 'Accès / rencontre',
    procedure: 'Procédure / pression',
  },
};

export const SCHEMATIC_MAX_NODES = 24;
export const SCHEMATIC_MAX_LINKS = 60;
export const SCHEMATIC_MAX_COLUMNS = 5;
export const SCHEMATIC_LABEL_CHARS = 80;

export interface SchematicNode {
  id: string;
  label: string;
  /** A second line ("think tank", "DG NEAR"), or none. */
  sublabel?: string | null;
  /** Column, 0 = leftmost. */
  column: number;
  /** Color slot (1–12) of the box's left edge; null = neutral. */
  slot?: number | null;
}

export interface SchematicLink {
  from: string;
  to: string;
  kind: SchematicLinkKind;
  label?: string | null;
}

export interface SchematicSvgInput {
  title: string;
  nodes: readonly SchematicNode[];
  links: readonly SchematicLink[];
  /** Legend language (English by default). */
  language?: 'en' | 'fr';
  source?: string | null;
}

const PAD = 20;
const BOX_H = 44;
const ROW_GAP = 18;
const TOP = 44;
const LEGEND_H = 24;

export function schematicSvg(input: SchematicSvgInput): { svg: string; width: number; height: number } {
  const width = FIGURE_WIDTH;
  const nodes = input.nodes.slice(0, SCHEMATIC_MAX_NODES);
  const ids = new Set(nodes.map(n => n.id));
  const links = input.links.filter(l => ids.has(l.from) && ids.has(l.to) && l.from !== l.to).slice(0, SCHEMATIC_MAX_LINKS);
  const lang = input.language ?? 'en';
  if (!nodes.length) {
    const h = TOP + 40;
    return {
      svg: svgFrame(
        width,
        h,
        input.title,
        figureTitle(input.title) + svgLabel('Nothing to draw.', PAD, TOP + 16, { fill: PRINT_COLORS.muted }),
      ),
      width,
      height: h,
    };
  }
  // Columns: the distinct column numbers in order, each box in its column in the order given.
  const columns = [...new Set(nodes.map(n => Math.max(0, Math.min(SCHEMATIC_MAX_COLUMNS - 1, Math.floor(n.column)))))].sort(
    (a, b) => a - b,
  );
  const colIndex = new Map(columns.map((c, i) => [c, i]));
  const colW = (width - 2 * PAD) / columns.length;
  const boxW = Math.min(200, colW - 36);
  const rowsIn = new Map<number, number>();
  const pos = new Map<string, { x: number; y: number }>();
  for (const nd of nodes) {
    const ci = colIndex.get(Math.max(0, Math.min(SCHEMATIC_MAX_COLUMNS - 1, Math.floor(nd.column)))) ?? 0;
    const row = rowsIn.get(ci) ?? 0;
    rowsIn.set(ci, row + 1);
    pos.set(nd.id, { x: PAD + ci * colW + (colW - boxW) / 2, y: TOP + row * (BOX_H + ROW_GAP) });
  }
  const rows = Math.max(...rowsIn.values());
  const plotBottom = TOP + rows * (BOX_H + ROW_GAP) - ROW_GAP;

  const edges = links.map(l => {
    const a = pos.get(l.from) as { x: number; y: number };
    const b = pos.get(l.to) as { x: number; y: number };
    const forward = b.x > a.x;
    const sameCol = Math.abs(a.x - b.x) < 1;
    // Left to right: out of the right edge, into the left edge; within a column: out of the bottom, into the top.
    const x1 = sameCol ? a.x + boxW / 2 : forward ? a.x + boxW : a.x;
    const y1 = sameCol ? (b.y > a.y ? a.y + BOX_H : a.y) : a.y + BOX_H / 2;
    const x2 = sameCol ? b.x + boxW / 2 : forward ? b.x : b.x + boxW;
    const y2 = sameCol ? (b.y > a.y ? b.y : b.y + BOX_H) : b.y + BOX_H / 2;
    const mx = (x1 + x2) / 2;
    const d = sameCol
      ? `M${n1(x1)} ${n1(y1)}L${n1(x2)} ${n1(y2)}`
      : `M${n1(x1)} ${n1(y1)}C${n1(mx)} ${n1(y1)} ${n1(mx)} ${n1(y2)} ${n1(x2)} ${n1(y2)}`;
    const dash = LINK_DASH[l.kind];
    const path = `<path d="${d}" fill="none" stroke="${PRINT_COLORS.foreground}" stroke-opacity="0.7" stroke-width="1.5"${
      dash ? ` stroke-dasharray="${dash}"` : ''
    } marker-end="url(#arrow)" data-link-kind="${l.kind}" data-from="${attrId(l.from)}" data-to="${attrId(l.to)}"/>`;
    const text = l.label
      ? svgLabel(l.label, mx, (y1 + y2) / 2 - 7, { size: 9, fill: PRINT_COLORS.muted, max: 24, halo: true, align: 'center' })
      : '';
    return path + text;
  });

  const boxes = nodes.map(nd => {
    const p = pos.get(nd.id) as { x: number; y: number };
    const color = PRINT_COLORS.entity(nd.slot ?? null);
    const maxChars = Math.max(8, Math.floor((boxW - 16) / 6.2));
    return [
      `<rect x="${n1(p.x)}" y="${n1(p.y)}" width="${n1(boxW)}" height="${BOX_H}" rx="4" fill="${PRINT_COLORS.background}" stroke="${PRINT_COLORS.line}" stroke-width="1" data-node-id="${attrId(nd.id)}"/>`,
      `<rect x="${n1(p.x)}" y="${n1(p.y)}" width="4" height="${BOX_H}" rx="2" fill="${color}"/>`,
      svgLabel(nd.label, p.x + 12, p.y + (nd.sublabel ? 16 : BOX_H / 2), { size: 11, weight: 600, max: maxChars }),
      nd.sublabel ? svgLabel(nd.sublabel, p.x + 12, p.y + 31, { size: 9.5, fill: PRINT_COLORS.muted, max: maxChars }) : '',
    ].join('');
  });

  // Legend: the four link kinds, only those used (all four when none is).
  const used = SCHEMATIC_LINK_KINDS.filter(k => links.some(l => l.kind === k));
  const kinds = used.length ? used : [...SCHEMATIC_LINK_KINDS];
  const ly = plotBottom + 26;
  const legend = kinds.map((k, i) => {
    const x = PAD + i * 180;
    const dash = LINK_DASH[k];
    return `<line x1="${x}" y1="${ly}" x2="${x + 28}" y2="${ly}" stroke="${PRINT_COLORS.foreground}" stroke-width="2"${
      dash ? ` stroke-dasharray="${dash}"` : ''
    }/>${svgLabel(SCHEMATIC_LINK_LABELS[lang][k], x + 34, ly, { size: 10, fill: PRINT_COLORS.muted, max: 24 })}`;
  });
  const foot = input.source ? footLines(wrapText(input.source, 135, 2), ly + LEGEND_H) : null;
  const height = Math.round(ly + LEGEND_H + (foot ? foot.height : 0) + 4);
  const body = [
    `<defs><marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0 10 5 0 10Z" fill="${PRINT_COLORS.foreground}" fill-opacity="0.7"/></marker></defs>`,
    figureTitle(input.title),
    `<g>${edges.join('')}</g>`,
    `<g>${boxes.join('')}</g>`,
    `<g>${legend.join('')}</g>`,
    foot?.svg ?? '',
  ].join('\n');
  const desc = `${nodes.length} boxes, ${links.length} links: ${links
    .map(
      l => `${nodes.find(n => n.id === l.from)?.label} → ${nodes.find(n => n.id === l.to)?.label} (${SCHEMATIC_LINK_LABELS[lang][l.kind]})`,
    )
    .join('; ')}`;
  return { svg: svgFrame(width, height, `${input.title}. ${desc}`, body), width, height };
}

/** The links as a table (the schematic's data table): from, link kind, to, label. */
export function schematicTable(
  input: Pick<SchematicSvgInput, 'nodes' | 'links' | 'language'>,
  headers: readonly [string, string, string, string],
): { headers: string[]; rows: string[][] } {
  const lang = input.language ?? 'en';
  const name = (id: string) => input.nodes.find(n => n.id === id)?.label ?? id;
  return {
    headers: [...headers],
    rows: input.links.map(l => [name(l.from), SCHEMATIC_LINK_LABELS[lang][l.kind], name(l.to), l.label ?? '']),
  };
}
